import { useEffect, useId, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';

interface Props {
  open: boolean;
  /** Etapa destino en texto legible ("elaboración" / "revisión"). */
  toLabel: string;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  /** G2: el mismo modal pide el motivo de un avance forzado por Calidad. */
  title?: string;
  confirmLabel?: string;
  fieldLabel?: string;
  placeholder?: string;
  destructive?: boolean;
  /** Consecuencia que el usuario debe conocer antes de confirmar (p. ej. reabrir una firma). */
  warning?: string;
}

/** Motivo obligatorio que queda en la trazabilidad: devolución por reproceso y, en G2, el
 *  avance forzado de Calidad. Modal accesible con focus trap; confirmar se habilita con
 *  motivo. Por defecto es la devolución. */
export function ReturnModal({
  open,
  toLabel,
  busy = false,
  onCancel,
  onConfirm,
  title,
  confirmLabel = 'Devolver',
  fieldLabel = 'Motivo de la devolución (obligatorio)',
  placeholder = 'Explica qué debe corregirse antes de reenviar',
  destructive = true,
  warning,
}: Props): ReactElement | null {
  const [reason, setReason] = useState('');
  const titleId = useId();
  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  if (!open) {
    return null;
  }
  const trimmed = reason.trim();
  return (
    <ModalShell
      open={open}
      title={title ?? `Devolver a ${toLabel}`}
      busy={busy}
      describedBy={warning === undefined ? undefined : `${titleId}-w`}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className={destructive ? 'btn sm danger' : 'btn sm'}
            onClick={() => onConfirm(trimmed)}
            disabled={busy || trimmed === ''}
          >
            {busy ? 'Procesando…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="modal-body">
        {warning !== undefined && (
          <p id={`${titleId}-w`} className="fld-warn">
            {warning}
          </p>
        )}
        <label htmlFor={`${titleId}-r`} className="fld-lab">
          {fieldLabel}
        </label>
        <textarea
          id={`${titleId}-r`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          maxLength={2000}
          placeholder={placeholder}
          style={{ width: '100%', marginTop: 6 }}
        />
      </div>
    </ModalShell>
  );
}
